import { Box, createStyles, makeStyles } from '@material-ui/core';
import * as React from 'react';
import NegotiationStateAlert from '../../../components/Alerts/NegotiationStateAlert';
import Confetti from '../../../components/Confetti/Confetti';
import AlertBase from '../../../components/Alerts/AlertBase';
import { getNegBooleans } from '../../../utils/getNegBooleans';
import DebtDetailsNegotiationConfirmationStateImage from './DebtDetailsNegotiationConfirmationStateImage';
import DebtDetailsNegotiationConfirmationNegTerms from './DebtDetailsNegotiationConfirmationNegTerms';
import { NegotiationOption, NegotiationState, OutDebt } from '../../../generated/graphql';
import CertificationCard from '../../../components/NegotiationDebts/CertificationCard';
import { TranquiTheme } from '../../App/theme';
import { DebtDetailsNegotiationConfirmationExpirationAction } from './DebtDetailsNegotiationConfirmationExpirationAction';
import useDictionary from '../../../dictionaries/context/useDictionary';
import MarkdownRender from '../../../dictionaries/MarkdownRender';

interface DebtDetailsNegConfirmationProps {
  outDebt: OutDebt;
  negOptions: NegotiationOption[];
  outDebts?: OutDebt[];
  onReset: () => void;
}

const DebtDetailsNegotiationConfirmation: React.FC<DebtDetailsNegConfirmationProps> = (props) => {
  const { outDebt, outDebts, negOptions, onReset } = props;

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const { gT } = useDictionary();
  const { negotiation } = outDebt;

  if (!negotiation || !negotiation?.state) return null;

  const {
    negState, isTotalPayment, isPaymentPromise, isRestructure,
    isRappiPay, isPichincha, isBeta, isConfirmedByUser, isJcap,
    isMovistarMoney, isInstallmentsPlan, isApprovedByBank, isCatchUp,
    isBbva, isBogota, isScotiabank, isApex, isPra,
    isBogotaTermExtension,
  } = getNegBooleans(outDebt);
  const { alternative, negotiationOption } = negotiation;

  const isDocumentalTermExtension = isBogotaTermExtension
    && negotiationOption?.settings?.isDocumentalTermExtension;

  const documentalAlert = isDocumentalTermExtension ? (
    <AlertBase type="informative" className="my-2" key="documentalAlert">
      <MarkdownRender text={gT('termExtension_documental_alert')} />
    </AlertBase>
  ) : null;

  const noCertification = isJcap
    || isPaymentPromise
    || (isRestructure && !isRappiPay)
    || (isRappiPay && !isTotalPayment && !isInstallmentsPlan && !isRestructure)
    || (isBbva && isTotalPayment)
    || (isPichincha && !(isTotalPayment && (isConfirmedByUser || isApprovedByBank)))
    || isBeta
    || (isMovistarMoney && !isTotalPayment && !isCatchUp)
    || (isScotiabank)
    || (isPra)
    || (isApex && !isMovistarMoney);

  const negotiationAlert = (
    <React.Fragment key="alert">
      <NegotiationStateAlert
        outDebt={outDebt}
        option={negotiationOption}
        negOptions={negOptions}
        alternative={alternative}
        outDebts={outDebts}
        className="my-3"
      />
    </React.Fragment>
  );

  const negotiationNegTerms = (
    <DebtDetailsNegotiationConfirmationNegTerms
      key="terms"
      outDebt={outDebt}
      outDebts={outDebts || []}
      negOptions={negOptions}
    />
  );

  const negotiationCertificationCard = noCertification ? undefined : (
    <CertificationCard
      key="certificate"
      bank={outDebt.bank}
      noBalance={isTotalPayment && !outDebt.balance}
      negotiationId={negotiation.id}
    />
  );

  const useExpirationAction = negState === NegotiationState.Expired
    || (isJcap && negState === NegotiationState.RejectedByBank)
    || (isBogota && negState === NegotiationState.RejectedByBank && !isBogotaTermExtension);
  const expirationAction = (
    <DebtDetailsNegotiationConfirmationExpirationAction
      key="expirationAction"
      outDebt={outDebt}
      negOptions={negOptions}
      outDebts={outDebts || []}
      onReset={onReset}
    />
  );

  const confetti = <Confetti key="confetti" />;

  const content = (() => {
    switch (negState) {
      case NegotiationState.ConfirmedByUser:
        return [ confetti, negotiationAlert,
          documentalAlert, negotiationNegTerms, negotiationCertificationCard ];

      case NegotiationState.PrepaymentCompleted:
        return [ confetti, negotiationAlert,
          documentalAlert, negotiationNegTerms, negotiationCertificationCard ];

      case NegotiationState.SentToBank:
        return [ confetti, negotiationAlert,
          documentalAlert, negotiationNegTerms, negotiationCertificationCard ];

      case NegotiationState.ApprovedByBank:
        if (isTotalPayment) {
          return [ confetti, negotiationCertificationCard,
            negotiationAlert, negotiationNegTerms ];
        }
        return [ confetti, negotiationAlert,
          negotiationNegTerms, negotiationCertificationCard ];

      case NegotiationState.PrepaymentIncomplete:
      case NegotiationState.RejectedByBank:
        return [
          negotiationAlert, negotiationNegTerms, ...useExpirationAction ? [ expirationAction ] : [],
        ];

      default:
      case NegotiationState.Expired:
        return [ negotiationAlert, negotiationNegTerms, expirationAction ];
    }
  })();

  return (
    <Box className={classes.boxContainer}>
      <DebtDetailsNegotiationConfirmationStateImage outDebt={outDebt} />
      {content}
    </Box>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  boxContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'stretch',
    maxWidth: 500,
    margin: 'auto',
  },
  secondAlert: {
    marginBottom: theme.spacing(2),
    '& svg': {
      display: 'none',
    },
    '& ul': {
      paddingLeft: theme.spacing(3),
      margin: '2px 0 0',
    },
  },
}));

export default DebtDetailsNegotiationConfirmation;
